import type { CSSProperties } from "react";
import { shortenName } from "./shortenName";
import type { LabelStyle, UnitDisplaySettings } from "./types";

const BASE_CLASSES = "flex items-center gap-1.5 max-w-[200px] leading-none whitespace-nowrap pointer-events-auto cursor-pointer";

const STYLE_CLASSES = {
  card: "text-gray-900 px-2 py-1 rounded-md border border-black/10 shadow-md",
  halo: "text-gray-900 px-1",
} as const satisfies Record<LabelStyle, string>;

const NAME_WEIGHT_CLASSES = {
  card: "font-semibold",
  halo: "font-bold",
} as const satisfies Record<LabelStyle, string>;

const SPEED_COLOR_CLASSES = {
  card: "text-gray-600",
  halo: "text-gray-700",
} as const satisfies Record<LabelStyle, string>;

const HALO_TEXT_STYLE = {
  WebkitTextStroke: "3px #ffffff",
  paintOrder: "stroke fill",
  textShadow: "0 0 3px #ffffff",
} as const satisfies CSSProperties;

interface Props {
  settings: UnitDisplaySettings;
  name: string;
  speed: string | null | undefined;
  statusColor?: string;
  positionClassName: string;
  style?: CSSProperties;
}

export default function UnitLabel({ settings, name, speed, statusColor, positionClassName, style }: Props) {
  const showName = settings.name_mode !== "none";
  const unitOnly = settings.name_mode === "unit";
  const showSpeed = settings.show_speed && !!speed;
  if (!showName && !showSpeed) return null;

  const isCard = settings.label_style === "card";

  return (
    <div
      title={unitOnly ? name : undefined}
      className={`${positionClassName} ${BASE_CLASSES} ${STYLE_CLASSES[settings.label_style]}`}
      style={{
        ...style,
        fontSize: `${settings.label_px}px`,
        ...(isCard ? { backgroundColor: `rgba(255, 255, 255, ${settings.card_opacity})` } : HALO_TEXT_STYLE),
      }}
    >
      {settings.status_dot && statusColor && (
        <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: statusColor }} />
      )}
      {showName && (
        <span className={`${NAME_WEIGHT_CLASSES[settings.label_style]} truncate`}>{unitOnly ? shortenName(name) : name}</span>
      )}
      {showSpeed && (
        <span
          className={`shrink-0 tabular-nums ${SPEED_COLOR_CLASSES[settings.label_style]}`}
          style={{ fontSize: `${settings.speed_px}px` }}
        >
          {speed}
        </span>
      )}
      {/* Piquito hacia la unidad: sin él la tarjeta parecía flotar suelta sobre
          el mapa, sobre todo con vehículos chicos al alejar (Daniel, 21-oct-2026,
          «se ven raras»). Mide 5 px; los huecos del marcador ya lo descuentan. */}
      {isCard && (
        <span
          aria-hidden
          className="absolute left-1/2 top-full -translate-x-1/2 h-0 w-0 border-x-[5px] border-t-[5px] border-x-transparent"
          style={{ borderTopColor: `rgba(255, 255, 255, ${settings.card_opacity})`, filter: "drop-shadow(0 1px 0 rgba(0,0,0,0.12))" }}
        />
      )}
    </div>
  );
}
